import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from 'api/v1/api';
import { WALLET_PUBLIC_ENABLED } from 'constants/walletPublic';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

const walletQueriesEnabled = (session: ReturnType<typeof useAuthSession>['data']) =>
  WALLET_PUBLIC_ENABLED && isAuthSessionActive(session);

export type WalletTransaction = {
  id: number;
  type: string;
  type_label: string;
  amount_rub: string;
  balance_after_rub: string;
  description?: string | null;
  receipt_number?: string | null;
  created_at?: string | null;
  meta?: Record<string, unknown>;
};

export type WalletRefundRequest = {
  id: number;
  amount_rub: string;
  status: string;
  status_label: string;
  reason?: string | null;
  rejection_reason?: string | null;
  created_at?: string | null;
  processed_at?: string | null;
};

export type WalletSummary = {
  available_rub: string;
  reserved_rub: string;
  currency: string;
  provider: string;
  pending_refund_rub: string;
  recent_transactions: WalletTransaction[];
};

export type WalletTopUpCreated = {
  payment_order_id: number;
  checkout_token: string;
  checkout_url: string;
  amount_rub: string;
  provider: string;
  expires_at?: string | null;
};

export type WalletCheckoutSession = {
  checkout_token: string;
  amount_rub: string;
  provider: string;
  provider_label: string;
  status: string;
  return_url: string;
  expires_at?: string | null;
  test_mode: boolean;
};

const walletQueryKey = ['tutor-wallet', 'me'] as const;
const walletTransactionsKey = ['tutor-wallet', 'transactions'] as const;
const walletRefundsKey = ['tutor-wallet', 'refund-requests'] as const;

async function fetchWalletSummary(): Promise<WalletSummary> {
  const response = await api.get<WalletSummary>('/tutors/me/wallet');
  return response.data;
}

async function fetchWalletTransactions(params: {
  limit: number;
  offset: number;
  period?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const response = await api.get<{ items: WalletTransaction[]; total: number }>(
    '/tutors/me/wallet/transactions',
    {
      params: {
        limit: params.limit,
        offset: params.offset,
        period: params.period,
        date_from: params.dateFrom,
        date_to: params.dateTo,
      },
    },
  );
  return response.data;
}

async function fetchCheckoutSession(token: string): Promise<WalletCheckoutSession> {
  const response = await api.get<WalletCheckoutSession>(`/tutors/me/wallet/checkout/${token}`);
  return response.data;
}

export function useTutorWallet() {
  const { data: session } = useAuthSession();

  return useQuery({
    queryKey: walletQueryKey,
    queryFn: fetchWalletSummary,
    enabled: walletQueriesEnabled(session),
    staleTime: 30_000,
  });
}

export function useTutorWalletTransactions(params: {
  limit?: number;
  offset?: number;
  period?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const { data: session } = useAuthSession();
  const limit = params.limit ?? 10;
  const offset = params.offset ?? 0;

  return useQuery({
    queryKey: [...walletTransactionsKey, limit, offset, params.period, params.dateFrom, params.dateTo],
    queryFn: () =>
      fetchWalletTransactions({
        limit,
        offset,
        period: params.period,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      }),
    enabled: walletQueriesEnabled(session),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function useWalletCheckoutSession(token: string | undefined) {
  const { data: session } = useAuthSession();

  return useQuery({
    queryKey: ['tutor-wallet', 'checkout', token],
    queryFn: () => fetchCheckoutSession(token!),
    enabled: WALLET_PUBLIC_ENABLED && Boolean(token) && isAuthSessionActive(session),
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 4000 : false;
    },
  });
}

export function useWalletTopUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (amountRub: number) => {
      const response = await api.post<WalletTopUpCreated>('/tutors/me/wallet/top-up', {
        amount_rub: amountRub,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: walletQueryKey });
    },
  });
}

export function useWalletRefundRequests() {
  const { data: session } = useAuthSession();

  return useQuery({
    queryKey: walletRefundsKey,
    queryFn: async () => {
      const response = await api.get<WalletRefundRequest[]>('/tutors/me/wallet/refund-requests');
      return response.data;
    },
    enabled: walletQueriesEnabled(session),
    staleTime: 30_000,
  });
}

export function useWalletRefundRequestCreate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { amount_rub: number; reason?: string }) => {
      const response = await api.post<WalletRefundRequest>(
        '/tutors/me/wallet/refund-requests',
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: walletQueryKey });
      queryClient.invalidateQueries({ queryKey: walletTransactionsKey });
      queryClient.invalidateQueries({ queryKey: walletRefundsKey });
    },
  });
}
